import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Modal,
    IconButton,
    Switch,
    Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { Info } from '@mui/icons-material';
import { formatDate } from '../../utils/DateFormat';
import { LoadingButton } from '@mui/lab';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import styles from './style';

const ApplyCreditsToInvoices = ({
    open,
    setOpen,
    loading,
    handleApply,
    creditNote,
    invoicesList,
    item,
    getInvoiceList
}) => {
    const [invoices, setInvoices] = useState([]);
    const [setAppliedDate, setSetAppliedDate] = useState(true);
    const [selectedDate, setSelectedDate] = useState(dayjs());

    useEffect(() => {
        if (open && item?.customer?._id) {
            getInvoiceList(item.customer._id);
        }
    }, [open]);

    const parseAmount = (amount) => {
        if (typeof amount === 'number') return amount;
        if (!amount || typeof amount !== 'string') return 0;
        const parsed = parseFloat(amount.replace(/[^0-9.-]/g, ''));
        return isNaN(parsed) ? 0 : parsed;
    };
    const actualCreditAmount = parseAmount(creditNote?.balance);

    // Calculate total credits applied by summing all creditsToApply values
    const creditsApplied = useMemo(() => {
        const total = invoices.reduce((sum, invoice) => {
            return sum + parseAmount(invoice.creditsToApply);
        }, 0);
        return total.toFixed(2);
    }, [invoices]);

    // Calculate remaining credits: Credit Note Balance - Credits Applied
    const remainingCredits = useMemo(() => {
        const remaining = actualCreditAmount - parseFloat(creditsApplied);
        return Math.max(0, remaining).toFixed(2);
    }, [actualCreditAmount, creditsApplied]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = () => {
        const invoicesWithCredits = invoices
            .filter(invoice => invoice.creditsToApply && parseFloat(invoice.creditsToApply) > 0)
            .map(invoice => ({
                invoiceId: invoice._id,
                invoiceNo: invoice.invoiceNo,
                date: setAppliedDate ? selectedDate.toISOString() : new Date().toISOString(),
                amount: parseFloat(invoice.creditsToApply)
            }));


        if (invoicesWithCredits.length > 0) {
            handleApply({ invoices: invoicesWithCredits, creditNoteId: creditNote?._id });
            getInvoiceList(item?.customer._id);
        }
        handleClose();
    };

    const handleCreditChange = (index, value) => {
        if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;

        // If value is not empty, check if it exceeds actualCreditAmount
        const currentInvoice = invoices[index];
        if (value !== '') {
            const numeric = parseFloat(value);
            if (numeric > actualCreditAmount) return;

            if (numeric > currentInvoice.amountDue) return;
        }
        
        setInvoices(prevInvoices => {
            return prevInvoices.map((invoice, i) => {
                if (i === index) {
                    return {
                        ...invoice,
                        creditsToApply: value,
                    };
                }
                return invoice;
            });
        });
    };

    useEffect(() => {
        if (invoicesList && invoicesList.length > 0) {
            setInvoices(invoicesList.map(invoice => ({
                ...invoice,
                creditsToApply: '', // Initialize as empty string
                originalAmountDue: parseAmount(invoice.amountDue),
            })));
        }
    }, [invoicesList]);

    return (
        <Modal
            open={open}
            onClose={handleClose}
            BackdropProps={{
                style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
            }}
        >
            <Box sx={styles.modalContainer}>
                <IconButton sx={styles.closeButton} onClick={handleClose}>
                    <CloseIcon />
                </IconButton>

                <Box sx={styles.header}>
                    <Typography sx={styles.headerTitle}>Apply Credits to Invoices</Typography>
                </Box>

                <Box sx={styles.creditInfo}>
                    <Box sx={styles.creditBox}>
                        <Box className="note" sx={styles.creditIcon}>
                            <DescriptionIcon color='white' />
                        </Box>
                        <Box sx={styles.creditDetails}>
                            <Typography className="label">Credit Note#</Typography>
                            <Typography className="value">{creditNote?.creditNoteNumber || ''}</Typography>
                        </Box>
                    </Box>

                    <Box sx={styles.creditBox}>
                        <Box className="available" sx={styles.creditIcon}>
                            <AccountBalanceWalletIcon color='white' />
                        </Box>
                        <Box sx={styles.creditDetails}>
                            <Typography className="label">Available Credits</Typography>
                            <Typography className="value">{creditNote?.balance || ''}</Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={styles.sectionHeader}>
                    <Typography sx={styles.sectionTitle}>Invoices to Apply</Typography>
                    <Box sx={styles.dateToggle}>
                        <Typography>Set Applied on Date</Typography>
                        <Tooltip
                            title="The most recent of the Invoice Date or Credit Note Date will be set here."
                            arrow
                            placement="top"
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: styles.tooltip.bgcolor,
                                        color: styles.tooltip.color,
                                        fontSize: styles.tooltip.fontSize,
                                        borderRadius: styles.tooltip.borderRadius,
                                        p: styles.tooltip.p,
                                        maxWidth: styles.tooltip.maxWidth,
                                        whiteSpace: 'normal',
                                    }
                                }
                            }}
                        >
                            <IconButton size="small" sx={styles.infoIcon} aria-label="info">
                                <Info fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Switch
                            checked={setAppliedDate}
                            onChange={(e) => {
                                setSetAppliedDate(e.target.checked);
                                if (!e.target.checked) {
                                    setSelectedDate(dayjs());
                                }
                            }}
                            size="small"
                            color="primary"
                        />
                    </Box>
                </Box>

                <TableContainer sx={styles.tableContainer}>
                    <Table sx={styles.table}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ background: 'white !important' }}>Invoice Number</TableCell>
                                <TableCell sx={{ backgroundColor: 'white !important' }}>Invoice Date</TableCell>
                                <TableCell align="right" sx={{ backgroundColor: 'white !important' }}>Invoice Amount</TableCell>
                                <TableCell align="right" sx={{ backgroundColor: 'white !important' }}>Invoice Balance</TableCell>
                                <TableCell sx={{ backgroundColor: 'white !important' }}>Credits Applied On</TableCell>
                                <TableCell align="right" sx={{ backgroundColor: 'white !important' }}>Credits to Apply</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ maxHeight: '400px' }}>
                            {invoices
                                .map((invoice, originalIndex) => ({ ...invoice, originalIndex }))

                                .filter((invoice) => {
                                    return parseAmount(invoice.originalAmountDue) > 0 ||
                                        parseAmount(invoice.creditsToApply) > 0;
                                })
                                .map((invoice) => {
                                    return (
                                        <TableRow key={invoice._id || invoice.originalIndex}>
                                            <TableCell>
                                                <Typography sx={{ fontWeight: 500, color: '#333' }}>
                                                    {invoice?.invoiceNo || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{invoice.invoiceDate ? formatDate(invoice?.invoiceDate) : 'N/A'}</TableCell>
                                            <TableCell align="right">{invoice?.grandTotal?.toFixed(2) || ''}</TableCell>
                                            <TableCell align="right">{invoice?.amountDue?.toFixed(2) || ''}</TableCell>
                                            <TableCell>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        value={selectedDate}
                                                        onChange={(newValue) => {
                                                            if (setAppliedDate) {
                                                                setSelectedDate(newValue);
                                                            }
                                                        }}
                                                        inputFormat="DD/MM/YYYY"
                                                        disabled={!setAppliedDate}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                fullWidth
                                                                sx={{
                                                                    ...styles.datePicker,
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: '8px !important',
                                                                        overflow: 'hidden',
                                                                        backgroundColor: !setAppliedDate ? '#f5f5f5' : 'inherit'
                                                                    },
                                                                    '& .MuiInputAdornment-root': {
                                                                        paddingLeft: '5px'
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </LocalizationProvider>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TextField
                                                    type="text" // Changed from "number" to "text" for better control
                                                    size="small"
                                                    value={invoice.creditsToApply || ''} // Display the current value
                                                    sx={styles.creditsInput}
                                                    onChange={(e) => {
                                                        handleCreditChange(invoice.originalIndex, e.target.value);
                                                    }}
                                                    placeholder="0.00"
                                                    inputProps={{
                                                        inputMode: 'decimal',
                                                        pattern: '[0-9]*\\.?[0-9]*'
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={styles.summary}>
                    <Box>
                        <Typography className="label">Credits Applied:</Typography>
                        <Typography className="value">{creditsApplied}</Typography>
                    </Box>
                    <Box>
                        <Typography className="label">Remaining Credits:</Typography>
                        <Typography className="value">{remainingCredits}</Typography>
                    </Box>
                </Box>

                <Box sx={styles.actions}>
                    <LoadingButton
                        sx={styles.jobButtons}
                        variant="contained"
                        onClick={handleSave}
                        disabled={!invoices.some(inv => parseAmount(inv.creditsToApply) > 0)}
                        loading={loading}
                    >
                        Save
                    </LoadingButton>
                </Box>
            </Box>
        </Modal>
    );
};

export default ApplyCreditsToInvoices;